import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
    IconButton
} from '@mui/material';
import { Close, CameraAlt } from '@mui/icons-material';
import QrScanner from 'qr-scanner';
import jsQR from 'jsqr';

const QRCodeScanner = ({ open, onClose, onScanSuccess }) => {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const videoRef = useRef(null);
    const qrScannerRef = useRef(null);
    const jsqrIntervalRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        if (open) {
            initializeScanner();
        } else {
            stopScanning();
        }

        return () => {
            stopScanning();
        };
    }, [open]);

    const initializeScanner = async () => {
        try {
            setError('');
            setLoading(true);
            setIsScanning(false);

            // Check if camera is available
            const hasCamera = await QrScanner.hasCamera();
            if (!hasCamera) {
                throw new Error('No camera found on this device');
            }

            // Set up camera constraints for optimal performance (higher resolution for better detection)
            const constraints = {
                video: {
                    facingMode: { exact: 'environment' }, // Force back camera
                    width: { ideal: 1280, max: 1280 }, // Higher resolution for better QR detection
                    height: { ideal: 720, max: 720 },
                    frameRate: { ideal: 30, max: 30 }, // Higher frame rate for smoother scanning
                    focusMode: 'continuous',
                    exposureMode: 'continuous',
                    whiteBalanceMode: 'continuous'
                }
            };

            // Get user media with constraints
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            videoRef.current.srcObject = stream;

            // Create QR scanner instance with optimized settings
            const qrScanner = new QrScanner(
                videoRef.current,
                (result) => {
                    console.log('QR Code detected:', result.data);
                    handleScanSuccess(result.data);
                },
                {
                    onDecodeError: (error) => {
                        // Ignore not found errors (normal during scanning)
                        if (error.name !== 'NotFoundException') {
                            console.warn('QR decode error:', error);
                        }
                    },
                    highlightScanRegion: false, // Disable for better performance
                    highlightCodeOutline: false, // Disable for better performance
                    preferredCamera: 'environment',
                    maxScansPerSecond: 15, // Further increased for faster scanning
                    calculateScanRegion: (video) => {
                        // Optimize scan region to center crop area (60% width, 30% height)
                        const cropWidth = Math.round(video.videoWidth * 0.6);
                        const cropHeight = Math.round(video.videoHeight * 0.3);
                        const x = Math.round((video.videoWidth - cropWidth) / 2);
                        const y = Math.round((video.videoHeight - cropHeight) / 2);
                        return {
                            x,
                            y,
                            width: cropWidth,
                            height: cropHeight,
                        };
                    },
                    // Additional performance optimizations
                    returnDetailedScanResult: false, // Reduce data processing
                }
            );

            qrScannerRef.current = qrScanner;

            // Start scanning
            await qrScanner.start();

            // Start jsQR fallback scanning
            startJsQRFallback();

            setIsScanning(true);
            setLoading(false);

        } catch (err) {
            console.error('Scanner initialization error:', err);
            
            // Try fallback initialization without custom constraints
            try {
                console.log('Attempting fallback initialization...');
                const qrScanner = new QrScanner(
                    videoRef.current,
                    (result) => {
                        console.log('QR Code detected:', result.data);
                        handleScanSuccess(result.data);
                    },
                    {
                        onDecodeError: (error) => {
                            if (error.name !== 'NotFoundException') {
                                console.warn('QR decode error:', error);
                            }
                        },
                        highlightScanRegion: false,
                        highlightCodeOutline: false,
                        preferredCamera: 'environment',
                        maxScansPerSecond: 8, // Slightly lower for fallback
                        returnDetailedScanResult: false,
                    }
                );

                qrScannerRef.current = qrScanner;
                await qrScanner.start();
                
                // Start jsQR fallback for fallback mode too
                startJsQRFallback();
                
                setIsScanning(true);
                setLoading(false);
                return;
            } catch (fallbackErr) {
                console.error('Fallback initialization also failed:', fallbackErr);
            }
            
            setError(`Camera error: ${err.message || 'Failed to initialize camera'}`);
            setLoading(false);
        }
    };

    const startJsQRFallback = () => {
        // Create canvas for jsQR processing
        if (!canvasRef.current) {
            canvasRef.current = document.createElement('canvas');
        }

        // Use requestAnimationFrame for better performance
        const scanFrame = () => {
            if (!isScanning || !videoRef.current || !videoRef.current.videoWidth) {
                if (isScanning) {
                    jsqrIntervalRef.current = requestAnimationFrame(scanFrame);
                }
                return;
            }

            try {
                const canvas = canvasRef.current;
                const ctx = canvas.getContext('2d');
                
                // Set canvas size to match video
                canvas.width = videoRef.current.videoWidth;
                canvas.height = videoRef.current.videoHeight;
                
                // Draw video frame to canvas
                ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
                
                // Calculate center crop area (60% width, 30% height - matching overlay)
                const cropWidth = canvas.width * 0.6;
                const cropHeight = canvas.height * 0.3;
                const cropX = (canvas.width - cropWidth) / 2;
                const cropY = (canvas.height - cropHeight) / 2;
                
                // Get cropped image data for faster processing
                const imageData = ctx.getImageData(cropX, cropY, cropWidth, cropHeight);
                
                // Scan for QR code using jsQR on cropped area
                const qrCode = jsQR(imageData.data, imageData.width, imageData.height, {
                    inversionAttempts: 'dontInvert'
                });
                
                if (qrCode && qrCode.data) {
                    console.log('QR Code detected (jsQR - center crop):', qrCode.data);
                    handleScanSuccess(qrCode.data);
                    return; // Stop scanning after detection
                }
            } catch (err) {
                console.warn('jsQR scan error:', err);
            }
            
            // Continue scanning
            if (isScanning) {
                jsqrIntervalRef.current = requestAnimationFrame(scanFrame);
            }
        };

        // Start the scanning loop
        jsqrIntervalRef.current = requestAnimationFrame(scanFrame);
    };

    const handleScanSuccess = (qrData) => {
        if (qrData) {
            setLoading(true);
            // Reduced delay for faster response
            setTimeout(() => {
                onScanSuccess(qrData);
                setLoading(false);
                stopScanning();
                onClose();
            }, 100); // Further reduced for faster response
        }
    };

    const stopScanning = () => {
        // Stop jsQR fallback scanning (requestAnimationFrame)
        if (jsqrIntervalRef.current) {
            cancelAnimationFrame(jsqrIntervalRef.current);
            jsqrIntervalRef.current = null;
        }

        if (qrScannerRef.current) {
            try {
                qrScannerRef.current.stop();
                qrScannerRef.current.destroy();
            } catch (err) {
                console.warn('Error stopping scanner:', err);
            }
            qrScannerRef.current = null;
        }
        
        // Clean up video stream
        if (videoRef.current && videoRef.current.srcObject) {
            const tracks = videoRef.current.srcObject.getTracks();
            tracks.forEach(track => {
                track.stop();
            });
            videoRef.current.srcObject = null;
        }
        
        setIsScanning(false);
    };

    const handleClose = () => {
        stopScanning();
        setError('');
        setLoading(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minHeight: '400px'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CameraAlt color="primary" />
                    <Typography variant="h6">Scan QR Code</Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, position: 'relative' }}>
                {error && (
                    <Alert 
                        severity="error" 
                        sx={{ m: 2 }}
                        onClose={() => setError('')}
                    >
                        {error}
                    </Alert>
                )}

                {loading && (
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        p: 3,
                        borderRadius: 2
                    }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            {isScanning ? 'Processing QR Code...' : 'Initializing Camera...'}
                        </Typography>
                    </Box>
                )}

                {!error && (
                    <Box sx={{ 
                        height: '300px',
                        overflow: 'hidden',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'black',
                        position: 'relative'
                    }}>
                        <video
                            ref={videoRef}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                            autoPlay
                            playsInline
                            muted
                        />
                        
                        {/* Visual scanning frame - matches center crop area (60% width, 30% height) */}
                        {isScanning && (
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '60%',
                                height: '30%',
                                border: '4px solid #ff0000',
                                borderRadius: '8px',
                                pointerEvents: 'none',
                                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
                                zIndex: 1,
                                boxSizing: 'border-box'
                            }} />
                        )}
                        
                        {!isScanning && !loading && (
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2,
                                color: 'white',
                                zIndex: 2
                            }}>
                                <CameraAlt sx={{ fontSize: 48, opacity: 0.7 }} />
                                <Typography variant="body2" align="center">
                                    Point your camera at a QR code
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {error && (
                    <Box sx={{
                        height: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2,
                        bgcolor: 'grey.50'
                    }}>
                        <CameraAlt sx={{ fontSize: 48, color: 'grey.400' }} />
                        <Typography variant="body2" color="text.secondary" align="center">
                            Camera not available. Please check your permissions or try again.
                        </Typography>
                        <Button 
                            variant="outlined" 
                            onClick={initializeScanner}
                            sx={{ mt: 1 }}
                        >
                            Retry
                        </Button>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button 
                    onClick={handleClose} 
                    variant="outlined"
                    fullWidth
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QRCodeScanner;